import { useState, useEffect, useCallback } from 'react';
import { useToast } from '../contexts/ToastContext';
import { getPaymentsForFacture, addPayment, updatePayment, deletePayment } from '../services/airtable';
import { formatEuro } from '../utils/formatters';

/**
 * usePaymentHistory - Hook pour gérer l'historique des paiements
 *
 * Extrait de PaymentHistoryModal.js pour une meilleure maintenabilité.
 *
 * @param {Object} facture - La facture concernée
 * @param {boolean} isOpen - État d'ouverture de la modale
 * @param {Function} onClose - Callback de fermeture
 * @param {Function} onPaymentAdded - Callback après ajout/modification
 */
export function usePaymentHistory(facture, isOpen, onClose, onPaymentAdded) {
  const { showToast } = useToast();

  // États des données
  const [payments, setPayments] = useState([]);
  const [factureInfo, setFactureInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // États d'édition
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [deleteConfirmPaymentId, setDeleteConfirmPaymentId] = useState(null);

  // Formulaire
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    montant: '',
    referenceBancaire: '',
    notes: ''
  });

  // === CHARGEMENT DES DONNÉES ===

  const loadPayments = useCallback(async () => {
    if (!facture) return;

    setLoading(true);
    try {
      const data = await getPaymentsForFacture(facture.id);
      setPayments(data.payments || []);
      setFactureInfo(data.facture || {});
    } catch (error) {
      console.error('Erreur loadPayments:', error);
      showToast(error.message || 'Erreur lors du chargement des paiements', 'error');
    } finally {
      setLoading(false);
    }
  }, [facture, showToast]);

  useEffect(() => {
    if (isOpen && facture) {
      loadPayments();
    }
  }, [isOpen, facture, loadPayments]);

  // === CALCULS ===

  const montantTTC = factureInfo?.['Montant total (TTC)'] || 0;
  const resteAPayer = factureInfo?.['Reste à payer'] || 0;
  const statutFacture = factureInfo?.['Statut'] || '';
  const isFacturePayee = resteAPayer === 0 && payments.length > 0;
  const isFacturePayeeSansHistorique = statutFacture === 'Payée' && resteAPayer === 0 && payments.length === 0;

  // Pour les factures "Emise" sans paiements, le reste à payer devrait être égal au montant TTC
  const resteAPayerEffectif = (statutFacture === 'Emise' && payments.length === 0 && resteAPayer === 0)
    ? montantTTC
    : resteAPayer;

  // === RÉINITIALISATION ===

  const resetForm = useCallback(() => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      montant: '',
      referenceBancaire: '',
      notes: ''
    });
  }, []);

  // === HANDLERS ===

  const handleAddPayment = useCallback(async () => {
    const errors = [];
    const montant = parseFloat(formData.montant);

    if (!montant || montant <= 0) {
      errors.push('Le montant doit être supérieur à 0');
    }

    if (montant > resteAPayerEffectif) {
      errors.push(`Le montant (${formatEuro(montant)}) dépasse le reste à payer (${formatEuro(resteAPayerEffectif)})`);
    }

    if (!formData.date) {
      errors.push('La date est requise');
    }

    if (new Date(formData.date) > new Date()) {
      errors.push('La date ne peut pas être dans le futur');
    }

    if (errors.length > 0) {
      showToast(errors.join('. '), 'error');
      return;
    }

    setSaving(true);
    try {
      const result = await addPayment({
        factureId: facture.id,
        montant,
        date: formData.date,
        referenceBancaire: formData.referenceBancaire || null,
        notes: formData.notes || null
      });

      showToast(
        result.facture.resteAPayer === 0
          ? 'Paiement ajouté. Facture complètement payée!'
          : `Paiement de ${formatEuro(montant)} ajouté avec succès`,
        'success'
      );

      await loadPayments();
      resetForm();

      // Si facture complètement payée, fermer après 2 secondes
      if (result.facture.resteAPayer === 0) {
        setTimeout(() => {
          handleClose();
        }, 2000);
      }

      if (onPaymentAdded) onPaymentAdded();
    } catch (error) {
      console.error('Erreur:', error);
      showToast(error.message || "Erreur lors de l'ajout du paiement", 'error');
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, facture, resteAPayerEffectif, showToast, loadPayments, resetForm, onPaymentAdded]);

  const handleEditPayment = useCallback((payment) => {
    setEditingPaymentId(payment.id);
    setFormData({
      date: payment.fields.Date,
      montant: payment.fields.Montant.toString(),
      referenceBancaire: payment.fields['Référence bancaire'] || '',
      notes: payment.fields.Notes || ''
    });
  }, []);

  const handleSaveEdit = useCallback(async (paymentId) => {
    const montant = parseFloat(formData.montant);

    if (!montant || montant <= 0) {
      showToast('Le montant doit être supérieur à 0', 'error');
      return;
    }

    setSaving(true);
    try {
      await updatePayment({
        paymentId,
        montant,
        date: formData.date,
        referenceBancaire: formData.referenceBancaire || null,
        notes: formData.notes || null
      });

      showToast('Paiement modifié avec succès', 'success');
      setEditingPaymentId(null);
      resetForm();
      await loadPayments();

      if (onPaymentAdded) onPaymentAdded();
    } catch (error) {
      console.error('Erreur:', error);
      showToast(error.message || 'Erreur lors de la modification du paiement', 'error');
    } finally {
      setSaving(false);
    }
  }, [formData, showToast, resetForm, loadPayments, onPaymentAdded]);

  const handleDeletePayment = useCallback(async () => {
    if (!deleteConfirmPaymentId) return;

    setSaving(true);
    try {
      await deletePayment(deleteConfirmPaymentId);
      showToast('Paiement supprimé avec succès', 'success');
      setDeleteConfirmPaymentId(null);
      await loadPayments();

      if (onPaymentAdded) onPaymentAdded();
    } catch (error) {
      console.error('Erreur:', error);
      showToast(error.message || 'Erreur lors de la suppression du paiement', 'error');
    } finally {
      setSaving(false);
    }
  }, [deleteConfirmPaymentId, showToast, loadPayments, onPaymentAdded]);

  const handleClose = useCallback(() => {
    setPayments([]);
    setFactureInfo(null);
    setEditingPaymentId(null);
    setDeleteConfirmPaymentId(null);
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const updateFormField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  return {
    // Données
    payments,
    factureInfo,
    loading,
    saving,

    // États calculés
    montantTTC,
    resteAPayerEffectif,
    isFacturePayee,
    isFacturePayeeSansHistorique,

    // États d'édition
    editingPaymentId,
    deleteConfirmPaymentId,
    setDeleteConfirmPaymentId,

    // Formulaire
    formData,
    updateFormField,

    // Handlers
    handleAddPayment,
    handleEditPayment,
    handleSaveEdit,
    handleDeletePayment,
    handleClose
  };
}
